<template>
  <o-modal
    ref="modal"
    :title="$t('service.edit')"
    :info="$t('components.reservationCustomer.actions.editService.info')"
    :options="[
      { text: $t('general.cancel'), type: 'cancel' },
      { text: $t('general.done'), type: 'confirm', callback: handleConfirm },
    ]"
    :valid="validate"
    modal-class="m-2 bg-gray-50 rounded-lg shadow-lg pointer-events-auto overflow-hidden sm:max-w-md sm:w-full h-full max-h-lg dark:bg-gray-900"
    content-class="mx-auto max-w-lg h-full flex flex-col bg-white dark:bg-gray-900"
  >
    <div v-if="reservationCustomer" class="text-left h-full flex flex-col">
      <div class="-mx-8 mb-4 -mt-5 flex-grow h-10">
        <o-scroll class="pb-4 px-8 h-full">
          <div v-if="reservationCustomer && service && employee" class="w-full bg-gray-50 border rounded-xl shadow-sm p-3 mb-6 dark:bg-gray-900">
            <div class="w-full flex flex-wrap md:flex-nowrap space-y-3 md:space-y-0 space-x-3 items-center">
              <div class="block self-stretch relative pr-1">
                <div
                  class="w-1 rounded h-full"
                  :style="{
                    backgroundColor: colour
                  }"
                />
              </div>

              <div class="pt-1">
                <p class="text-sm text-gray-700 dark:text-gray-200">
                  {{ startLabel }} - {{ finishLabel }}
                </p>
                <p class="text-base font-semibold leading-tight md:leading-loose my-1 md:my-0">
                  {{ service.title }}
                </p>
                <div class="text-sm text-gray-600 dark:text-gray-300 flex flex-wrap leading-none">
                  <span class="flex items-center mr-4 mb-1 leading-tight">
                    <o-avatar :items="[ employee ]" class="hidden md:flex pr-2" />
                    <span>{{ employee.displayName }}</span>
                  </span>
                  <div class="flex items-center">
                    <span class="flex items-center mr-4 mb-1 leading-tight">
                      <o-icon class="hidden md:block mr-2" icon="menuBookings" />
                      <span>{{ durationLabel }}</span>
                    </span>
                    <span class="flex items-center mr-4 mb-1 leading-tight">
                      <o-icon class="hidden md:block mr-2" icon="price" />
                      <span>{{ priceLabel }}</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="p-3 bg-gray-50 rounded-xl border dark:bg-gray-900">
            <o-form ref="form" class="h-full">
              <div class="flex items-center space-x-4 mb-6">
                <o-field-group
                  :label="$t('general.price')"
                  :name="$t('general.price')"
                  rules="required"
                  :modified="isPriceModified"
                  :modified-message="$t('service.priceModified')"
                  class="w-full"
                >
                  <template #default="{ status }">
                    <currency-field v-model="reservationCustomer.price" :status="status" />
                  </template>
                </o-field-group>
              </div>

              <template v-if="reservationCustomer.resources">
                <div class="w-full flex flex-wrap mt-4 -mx-2">
                  <template v-for="(resource, resourceIndex) in reservationCustomer.resources" :key="resourceIndex">
                    <o-field-group

                      :label="resource.serviceResource.name"
                      class="w-full md:w-1/2 px-2 mb-3"
                    >
                      <resource-select
                        v-model="reservationCustomer.resources[resourceIndex].resource"
                        class="w-full bg-white shadow-sm rounded dark:bg-gray-900"
                        :service="service"
                        :resource="resource"
                        :availability-options="{
                          service,
                          date,
                          resource,
                          reservationCustomers: [reservationCustomer],
                          startTime: service.startTime
                        }"
                        allow-none
                      />
                    </o-field-group>
                  </template>
                </div>
              </template>
            </o-form>
          </div>
        </o-scroll>
      </div>
    </div>
  </o-modal>
</template>

<script setup lang="ts">
import ResourceSelect from '@/components/resource/elements/Select.vue'

import { OAvatar } from '@/components/elements'

import {
  CurrencyField,
} from '@/components/fields'

import { useServiceStore } from '~/stores/useServiceStore'
import { useEmployeeStore } from '~/stores/useEmployeeStore'

interface Props {
  loading?: boolean
}

withDefaults(defineProps<Props>(), {
  loading: false,
})

const { $auth } = useNuxtApp()

const serviceStore = useServiceStore()
const employeeStore = useEmployeeStore()

const modal = ref<any>(null)
const form = ref<any>(null)

const reservationCustomer = ref<any>(null)
const resources = ref<any>(null)
const callback = ref<((data: any) => void) | null>(null)

const services = computed(() => serviceStore.all)
const employees = computed(() => employeeStore.all)

const reservation = computed(() => {
  return reservationCustomer.value?.reservation
})

const service = computed(() => {
  return reservation.value?.services[0]
})

const date = computed(() => {
  return reservation.value?.date?.value
})

const employee = computed(() => {
  return service.value?.employee
})

const colour = computed(() => {
  const svc = service.value?.service
  const clr = svc ? svc.borderHexColour : 'bbbbbb'

  return '#' + clr
})

const employeeOptions = computed(() => {
  const serviceDefaults = serviceStore.getServiceByID(reservationCustomer.value?.service?.idService)
  const emps = employees.value

  if (!serviceDefaults) {
    return []
  }

  const employeeIDs = serviceDefaults.employeeIDs

  return emps
    .filter(({ idEmployee }: any) => employeeIDs.includes(idEmployee))
})

const priceLabel = computed(() => {
  return $auth.info.location.currencySymbol + reservationCustomer.value?.price
})

const startLabel = computed(() => {
  return reservation.value?.startLabel
})

const finishLabel = computed(() => {
  return reservation.value?.finishLabel
})

const durationLabel = computed(() => {
  return service.value?.durationLabel
})

const isPriceModified = computed(() => {
  return reservationCustomer.value?.price !== service.value?.price
})

onMounted(() => {
  serviceStore.sync()
  employeeStore.sync()
})

function open(data: any, cb: (data: any) => void) {
  const rc = data.copy()

  if (rc.service) {
    const serviceDefaults = serviceStore.getServiceByID(rc.service.idService) || null

    rc.service = serviceDefaults
    rc.setResources(rc.resources)
  }

  reservationCustomer.value = rc
  callback.value = cb

  modal.value.open()
}

function validate() {
  return form.value.validate()
}

function handleConfirm() {
  const rc = reservationCustomer.value.copy()
  callback.value!(rc)
  return true
}

defineExpose({ open })
</script>
